import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  activationCode?: string;
}

const GoogleLogin: React.FC<GoogleLoginProps> = ({ 
  onSuccess, 
  onError,
  className = "",
  activationCode
}) => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Google Client ID - should match the one on the backend
    const googleClientId = "749906523453-h6oq4j1giievu0u4t6sc9oipal4lch2l.apps.googleusercontent.com";

    const handleGoogleLogin = async (response: any) => {
      try {
        const tokenId = response.credential;
        const success = await loginWithGoogle(tokenId, activationCode);
        
        if (success) {
          if (onSuccess) {
            onSuccess();
          } else {
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Google login error:', error);
        if (onError) {
          onError('Failed to login with Google. Please try again.');
        }
      }
    };

    // Load the Google Sign-In API script
    const loadGoogleScript = () => {
      if (typeof window.google !== 'undefined') {
        initializeGoogleLogin();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleLogin;
      document.body.appendChild(script);
    };

    // Initialize Google Sign-In
    const initializeGoogleLogin = () => {
      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleLogin,
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: 'popup',
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 250
        });
      }
    };

    loadGoogleScript();

    return () => {
      // Cleanup
      const scriptElement = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (scriptElement) {
        scriptElement.remove();
      }
    };
  }, [loginWithGoogle, navigate, onSuccess, onError, activationCode]);

  return (
    <div className={`flex justify-center ${className}`}>
      <div ref={googleButtonRef} className="google-login-button"></div>
    </div>
  );
};

export default GoogleLogin;